import { useForm as createUseForm } from "@ant-design-vue/use";
import { Col, Row } from "ant-design-vue";
import lodash from "lodash";
import moment from "moment";
import { defineAsyncComponent, reactive, toRaw } from "vue";
import { addModal, setIsLoading as setIsLoadingApp } from "../../../../app.logic";
import { setAppPageHeader } from "../../../../components/layout/app-layout/app-layout.logic";
import i18n from "../../../../i18n";
import store from "../../../../store";
import {
    createButton,
    createButtonActive,
    createButtonArchive,
    createButtonCreate,
    createButtonDelete,
    createButtonEdit,
    createButtonSave,
    createButtonView
} from "../../../../utils/button";
import { modalStyleIFrame } from "../../../../utils/constant";
import {
    checkAttribute,
    initUseForm,
    onClickActive,
    onClickActiveAll,
    onClickArchive,
    onClickArchiveAll,
    onClickCreate,
    onClickDelete,
    onClickDeleteAll,
    onClickEdit,
    onClickView
} from "../../../../utils/form";
import { createLoading } from "../../../../utils/loading";
import { has } from "../../../../utils/menu-context";
import {
    createAppPageHeaderTop,
    createManageActive,
    createManageArchive,
    createManageDelete,
    createManageExportCsv,
    createManageExtraCreate,
    createManageSelect,
    createPage,
    createRowActionEdit,
    createRowActionView
} from "../../../../utils/page";
import { flatWhereGroup } from "../../../../utils/search";
import { addRules, required, requiredWhen, validateError } from "../../../../utils/validate";
import { createConditionAction, createConditionForm } from "./company-calendar.condition";
import { callCompanyCalendarStatus, callInspectionType, createColumns, createField } from "./company-calendar.section";
import { createForm as createFormCompanyInspection } from "../company-request/company-inspection.logic";

const CIFrame = defineAsyncComponent(() => import("../../../../components/iframe/CIFrame"));
const CIcon = defineAsyncComponent(() => import("../../../../components/icon/CIcon"));

export const { isLoading, setIsLoading } = createLoading();

export const actionPrefix = "resource/v1/transaction/companyCalendar";
export const routerPrefix = "company-calendars";

export const callSave = ({ form }) => store.dispatch(`${actionPrefix}/save`, { data: form });
export const callSearch = ({ criteria }) => store.dispatch(`${actionPrefix}/search`, { criteria });
export const callActive = ({ id }) => store.dispatch(`${actionPrefix}/active`, { id });
export const callArchive = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id });
export const callDelete = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id, config: { params: { isHardDelete: true } } });
export const callActiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/activeAll`, { ids });
export const callArchiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids });
export const callDeleteAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids, config: { params: { isHardDelete: true } } });
export const callChanges = ({ id, criteria }) => store.dispatch(`${actionPrefix}/changes`, { id, criteria });

export const onSearch = ({ props, page, isSet = true }) => {
    const where = page.where || [];
    const sort = page.sort || [];
    setIsLoading(true);
    window.onSearch?.({ page });
    return callSearch({
        criteria: { ...page, data: [], where: [...where, ...createWhereRole({ props })], sort: [...sort, { name: "updateDate", type: "DESC" }] }
    })
        .then(page => (isSet ? (state.page = { ...page, where, sort }) : page))
        .finally(() => setIsLoading(false));
};

export const onClickSave = ({ props, data, refs, refStatus }) => {
    const { viewOnly } = props;
    return data.useForm
        .validate()
        .then(() => {
            setIsLoadingApp(true);
            return callSave({ form: refStatus ? { ...data.useForm.modelRef, refStatus } : data.useForm.modelRef })
                .finally(() => setIsLoadingApp(false))
                .then(record => {
                    if (viewOnly) initForm({ props, data, refs });
                    else onClickView({ record, routerPrefix });
                });
        })
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const onClickModalOk = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => ({ ...toRaw(data.useForm.modelRef), isSave: true }))
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const createSearchOneCriteria = ({ id, forLog }) => {
    if (!forLog) {
        return {
            select: ["**", "refCompanyInspection.**"],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }],
            children: [
                { name: "refCompany.companyProducts", select: ["**"], page: 0, size: 1000000, where: [{ name: "isActive", type: "Boolean", operator: "=", value: true }], sort: [] },
                { name: "refCompany.companyCountries", select: ["**", "refCountry.**"], page: 0, size: 1000000, sort: [] }
            ]
        };
    } else {
        return {
            select: [],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }],
            children: []
        };
    }
};

export const createFormTitle = ({ data }) => {
    const { modelRef, initialModel } = data.useForm;
    return modelRef.id
        ? `
        ${modelRef.refCompany?.nameTh} 
        ${modelRef.appointmentDatetime ? moment(modelRef.appointmentDatetime).format("DD/MM/YYYY HH:mm") : ""} 
        (${initialModel.refStatus?.value})
    `
        : i18n.global.t("button.create");
};

export const createWhereRole = ({ props }) => {
    const { menuContext } = props;
    const whereGroup = [];
    const userInfo = store.state.resource.auth.info?.user;
    if (has({ menuContext, key: "forCompany" })) {
        whereGroup.push([
            { name: "refCompany.userCompanies.refUser.username", type: "String", operator: "=", value: userInfo.username },
            { name: "refStatus.key", type: "String", operator: "!=", value: "Draft" }
        ]);
    }
    if (has({ menuContext, key: "forCompanyOfficer" })) {
        //
    }
    return flatWhereGroup({ where: whereGroup, logic: "OR" });
};

export const createForm = () => {
    return {
        refCompany: null,
        appointmentDatetime: null,
        appointmentPostponeDatetime: null,
        refInspectionType: null,
        refInspector: null,
        isInvestFollowup: null,
        refStatus: null,
        desc: null,
        isPostpone: null,
        companyRemark: null
    };
};

export const createRules = () => {
    return {
        refCompany: [required],
        appointmentDatetime: [required],
        refInspectionType: [required],
        refInspector: [required]
    };
};

export const loadForm = ({ props, data, refs }) => {
    setIsLoading(true);
    const { form } = props;
    const { id, useForm } = data;
    const { modelRef } = useForm;
    let promise = null;
    if (form) {
        promise = Promise.resolve({ data: [form] });
    } else if (id) {
        promise = callSearch({ criteria: createSearchOneCriteria({ id }) });
    } else {
        promise = Promise.resolve({ data: [modelRef] });
    }
    return promise
        .then(page => initUseForm({ props, data, refs, page, routerPrefix }))
        .then(() => loadDefaultForm({ props, data, refs }))
        .then(() => initByRole({ props, data, refs }))
        .then(() => initUseForm({ props, data, refs, page: { data: [modelRef] }, routerPrefix }))
        .finally(() => setIsLoading(false));
};

export const loadDefaultForm = ({ data }) => {
    const promises = [];
    promises.push(
        Promise.all([callInspectionType({}), callCompanyCalendarStatus({})]).then(arr => {
            data.inspectionTypes = arr[0];
            data.statuses = arr[1];
        })
    );
    return Promise.all(promises);
};

export const initState = ({ props }) => {
    const menuContext = props.menuContext;
    state.columns = createColumns({ props }).map(d => {
        return lodash.mergeWith(
            state.columns.find(d2 => d.dataIndex === d2.dataIndex),
            d,
            (objValue, srcValue, key) => {
                if (["searchValue"].includes(key)) return objValue;
            }
        );
    });
    state.manages = [
        createManageSelect({ menuContext }),
        createManageActive({
            menuContext,
            onClick: ({ rowSelection }) =>
                onClickActiveAll({ rowSelection, callActiveAll, state, onSearch: search => onSearch({ props, ...search }), setIsLoading })
        }),
        createManageArchive({
            menuContext,
            onClick: ({ rowSelection }) =>
                onClickArchiveAll({ rowSelection, callArchiveAll, state, onSearch: search => onSearch({ props, ...search }), setIsLoading })
        }),
        createManageDelete({
            menuContext,
            onClick: ({ rowSelection }) =>
                onClickDeleteAll({ rowSelection, callDeleteAll, state, onSearch: search => onSearch({ props, ...search }), setIsLoading }),
            getCheckboxProps: ({ record }) => ({ disabled: !["Draft"].includes(record?.refStatus?.key) })
        }),
        createManageExportCsv({ menuContext, callData: ({ page }) => onSearch({ props, page: { ...lodash.cloneDeep({ ...page, page: 0, size: 1000000 }) }, isSet: false }) })
    ];
    state.manageExtras = [createManageExtraCreate({ menuContext, onClick: () => onClickCreate({ routerPrefix }) })];
    state.rowActions = [
        createRowActionView({ menuContext, onClick: ({ record }) => onClickView({ record, routerPrefix }) }),
        createRowActionEdit({
            menuContext,
            onClick: ({ record }) => onClickEdit({ record, routerPrefix }),
            visible: ({ record }) => state.conditionAction.canEdit({ props, record })
        })
    ];
};

export const initForm = ({ props, data, refs }) => {
    checkAttribute({ props, data, refs });
    data.form = createForm({ props, data, refs, isLoading });
    data.rules = createRules({ props, data, refs, isLoading });
    data.useForm = createUseForm(data.form, data.rules);
    data.fields = createField({ props, data, refs, isLoading });
    data.conditionForms = createConditionForm({ props, data, refs, isLoading });
    loadForm({ props, data, refs }).then(() => {
        if (props.setAppPageHeader) {
            setAppPageHeader({
                slots: {
                    top: () => createAppPageHeaderTop({ props, data, refs, state, routerPrefix, onClickView, onClickEdit }),
                    default: () => createAppPageHeaderDefault({ props, data, refs })
                }
            });
        }
    });
};

export const initByRole = ({ props, data, key }) => {
    const { menuContext } = props;
    const { useForm } = data;
    const { modelRef, initialModel } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const initModel = key ? lodash.get(initialModel, key) : initialModel;
    const prefix = key ? `${key}.` : "";
    if (has({ menuContext, key: "forCompany" })) {
        if (["Wait"].includes(initModel.refStatus?.key)) {
            addRules({
                useForm,
                modelRef,
                key: `${prefix}refStatus`,
                rules: [
                    {
                        validator: () => {
                            if (!["Postpone", "Approve"].includes(model.refStatus?.key)) {
                                return Promise.reject(i18n.global.t("error.form.required"));
                            }
                            return Promise.resolve();
                        },
                        trigger: "change"
                    }
                ]
            });
            addRules({
                useForm,
                modelRef,
                key: `${prefix}appointmentPostponeDatetime`,
                rules: [
                    requiredWhen({
                        when: () => model.refStatus?.key === "Postpone"
                    })
                ]
            });
        }
    }
    if (has({ menuContext, key: "forCompanyOfficer" })) {
        if (["Postpone"].includes(initModel.refStatus?.key)) {
            addRules({
                useForm,
                modelRef,
                key: `${prefix}refStatus`,
                rules: [
                    {
                        validator: () => {
                            if (!["Approve", "Cancel"].includes(model.refStatus?.key)) {
                                return Promise.reject(i18n.global.t("error.form.required"));
                            }
                            return Promise.resolve();
                        },
                        trigger: "change"
                    }
                ]
            });
        }
        if (["Approve"].includes(initModel.refStatus?.key)) {
            if (!model.refCompanyInspection) {
                model.refCompanyInspection = createFormCompanyInspection({ props, data, key });
                model.refCompanyInspection.refCompany = model.refCompany;
                model.refCompanyInspection.refInspectionType = model.refInspectionType;
                model.refCompanyInspection.refInspector = model.refInspector;
                model.refCompanyInspection.isInvestFollowup = model.isInvestFollowup;
            }
            model.refCompanyInspection.isSave = true;
            addRules({ useForm, modelRef, key: `${prefix}refCompanyInspection.inspectionStartDatetime`, rules: [required] });
            addRules({ useForm, modelRef, key: `${prefix}refCompanyInspection.inspectionEndDatetime`, rules: [required] });
            addRules({
                useForm,
                modelRef,
                key: `${prefix}refCompanyInspection.c`,
                rules: [requiredWhen({ when: () => model.refCompanyInspection?.refInspectionType?.key === "1" })]
            });
            addRules({
                useForm,
                modelRef,
                key: `${prefix}refCompanyInspection.se`,
                rules: [requiredWhen({ when: () => model.refCompanyInspection?.refInspectionType?.key === "1" })]
            });
            addRules({
                useForm,
                modelRef,
                key: `${prefix}refCompanyInspection.m`,
                rules: [requiredWhen({ when: () => model.refCompanyInspection?.refInspectionType?.key === "1" })]
            });
            addRules({
                useForm,
                modelRef,
                key: `${prefix}refCompanyInspection.n`,
                rules: [requiredWhen({ when: () => model.refCompanyInspection?.refInspectionType?.key === "1" })]
            });
            //addRules({ useForm, modelRef, key: `${prefix}refCompanyInspection.refGmpLv`, rules: [requiredWhen({ when: () => model.refCompanyInspection?.refInspectionType?.key === '1' })] })
            addRules({ useForm, modelRef, key: `${prefix}refCompanyInspection.refGmpStatus`, rules: [required] });
            addRules({ useForm, modelRef, key: `${prefix}refCompanyInspection.refHaccpStatus`, rules: [required] });
        }
    }
};

export const createAppPageHeaderDefault = ({ props, data, refs }) => {
    const col1 = [];
    const col2 = [];
    const { menuContext, viewOnly } = props;
    const { useForm } = data;
    const { modelRef, initialModel } = useForm;
    const { id, isActive } = modelRef;
    const canEdit = state.conditionAction.canEdit({ props, record: initialModel });
    const canSendWait = state.conditionAction.canSendWait({ props, record: initialModel });
    const canApproveReview = state.conditionAction.canApproveReview({ props, record: initialModel });
    if (id && has({ menuContext, key: "canCreate" })) {
        col1.push(createButtonCreate({ onClick: () => onClickCreate({ routerPrefix }), classStyle: "m-1" }));
    }
    if (!viewOnly && id && has({ menuContext, key: "canView" })) {
        col1.push(createButtonView({ onClick: () => onClickView({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (viewOnly && id && isActive && has({ menuContext, key: "canEdit" }) && canEdit) {
        col1.push(createButtonEdit({ onClick: () => onClickEdit({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (!viewOnly && (!id || (id && isActive && canEdit))) {
        let options = null;
        if (["Wait", "Postpone"].includes(initialModel.refStatus?.key) && modelRef.refStatus?.key !== initialModel.refStatus?.key) {
            options = {
                text: `${viewOnly ? "" : `${i18n.global.t("button.save")} & `}${modelRef.refStatus?.attribute?.actionText}`,
                classStyle: `m-1 ${modelRef.refStatus?.attribute?.btnClass}`,
                confirm: { okButtonProps: { class: modelRef.refStatus?.attribute?.btnClass } }
            };
        }
        col1.push(
            createButtonSave({
                onClick: () => {
                    onClickSave({ props, data, refs });
                },
                classStyle: "m-1",
                options
            })
        );
    }
    if (id && isActive && canSendWait) {
        const statusWait = data.statuses.find(status => status.key === "Wait");
        col1.push(
            createButton({
                text: `${viewOnly ? "" : `${i18n.global.t("button.save")} & `}${statusWait?.attribute?.actionText}`,
                classStyle: `m-1 ${statusWait?.attribute?.btnClass}`,
                onClick: () => {
                    onClickSave({ props, data, refs, refStatus: statusWait });
                },
                icon: viewOnly ? null : <CIcon file="save.svg"></CIcon>,
                confirm: { okButtonProps: { class: statusWait.attribute?.btnClass } }
            })
        );
    }
    if (id && isActive && canApproveReview) {
        const statusApproveReview = data.statuses.find(status => status.key === "Approve Review");
        col1.push(
            createButton({
                text: `${viewOnly ? "" : `${i18n.global.t("button.save")} & `}${statusApproveReview?.attribute?.actionText}`,
                classStyle: `m-1 ${statusApproveReview?.attribute?.btnClass}`,
                onClick: () => {
                    onClickSave({ props, data, refs, refStatus: statusApproveReview });
                },
                icon: viewOnly ? null : <CIcon file="save.svg"></CIcon>,
                confirm: { okButtonProps: { class: statusApproveReview.attribute?.btnClass } }
            })
        );
    }
    if (viewOnly && id && !isActive && has({ menuContext, key: "canActive" })) {
        col2.push(
            createButtonActive({
                onClick: () =>
                    onClickActive({
                        record: { id },
                        callActive,
                        setIsLoading: setIsLoadingApp,
                        onClickEdit: () => onClickEdit({ record: { id }, routerPrefix })
                    }),
                classStyle: "m-1"
            })
        );
    }
    if (!viewOnly && id && isActive && has({ menuContext, key: "canArchive" })) {
        col2.push(
            createButtonArchive({
                onClick: () =>
                    onClickArchive({
                        record: { id },
                        callArchive,
                        setIsLoading: setIsLoadingApp,
                        onClickView: () => onClickView({ record: { id }, routerPrefix })
                    }),
                classStyle: "m-1"
            })
        );
    }
    if (!viewOnly && id && isActive && has({ menuContext, key: "canDelete" }) && ["Draft"].includes(initialModel.refStatus?.key)) {
        col2.push(
            createButtonDelete({
                onClick: () => onClickDelete({ record: { id }, callDelete, setIsLoading: setIsLoadingApp, state, routerPrefix }),
                classStyle: "m-1"
            })
        );
    }
    return (
        <Row type="flex">
            <Col>{col1}</Col>
            <Col class="ml-auto">{col2}</Col>
        </Row>
    );
};

export const openModalList = ({ bind, onIframeMounted }) => {
    const options = addModal({
        bind: lodash.merge(lodash.cloneDeep(modalStyleIFrame), bind),
        render: () => (
            <CIFrame
                src={`${process.env.VUE_APP_PUBLIC_PATH}/app/company/company-calendars?isLayout=false&canSelect=true&cacheQuery=true`}
                onIframeMounted={({ refs }) => onIframeMounted({ options, refs })}
            ></CIFrame>
        )
    });
    return options;
};

export const state = reactive({
    columns: [],
    page: createPage({ options: { select: ["**", "refCompany.refDepartment.*"] } }),
    tableOptions: { search: { activeKey: "Search" } },
    conditionAction: createConditionAction()
});
